import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DwExpressionStateService, DwQueryFilterGroup } from '@devwareapps/devware-cap';
import { Observable, Subject, throwError } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { ReportDashboardDefinition } from '../../models/report-dashboard-definition.model';
import { ReportDashboardGroupDefinition, ReportDashboardGroupTabDefinition } from '../../models/report-dashboard-group-definition.model';
import { ReportDashboardConfigService } from '../../services/report-dashboard-config.service';

/**
 * Chromeless host for embedding a single dashboard-framework-driven panel
 * (e.g. a KPI banner) inline on another page. Resolves the group's first
 * visible tab and renders its dashboard through the existing
 * ReportDashboardComponent - no bespoke tile-fetching or rendering logic
 * lives here. Not for full report pages: use
 * ConfiguredReportDashboardPageComponent for those (tabs, edit, print).
 */
@Component({
  selector: 'app-report-dashboard-group-host',
  templateUrl: './report-dashboard-group-host.component.html',
  styleUrls: ['./report-dashboard-group-host.component.scss']
})
export class ReportDashboardGroupHostComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dashboardGroupKey: string;
  @Input() useHomeSkeleton = false;
  @Input() expressionContext: any = {};
  @Input() scopeFilters: DwQueryFilterGroup;
  @Input() tileDrillDownRoute: string;

  definition: ReportDashboardDefinition;
  loading = false;
  errorMessage: string;

  private destroy$ = new Subject<void>();
  private loadGeneration = 0;

  constructor(
    private configService: ReportDashboardConfigService,
    private expressionState: DwExpressionStateService
  ) { }

  ngOnInit(): void {
    this.loadGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dashboardGroupKey && !changes.dashboardGroupKey.firstChange) {
      this.loadGroup();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadGroup(): void {
    const generation = ++this.loadGeneration;
    this.definition = null;
    this.errorMessage = null;

    if (!this.dashboardGroupKey) {
      this.errorMessage = 'A dashboardGroupKey is required.';
      return;
    }

    this.loading = true;
    this.configService.loadDashboardGroup(this.dashboardGroupKey).pipe(
      mergeMap(group => this.loadFirstVisibleDashboard(group)),
      takeUntil(this.destroy$)
    ).subscribe(definition => {
      if (generation !== this.loadGeneration) {
        return;
      }
      this.definition = definition;
      this.loading = false;
    }, error => {
      if (generation !== this.loadGeneration) {
        return;
      }
      this.loading = false;
      this.errorMessage = error?.message || error || 'The dashboard could not be loaded.';
    });
  }

  private loadFirstVisibleDashboard(group: ReportDashboardGroupDefinition): Observable<ReportDashboardDefinition> {
    const visibleTabs = (group.tabs || []).filter(tab => this.isVisible(tab));
    const tab = visibleTabs.find(item => item.dashboardKey === group.defaultDashboardKey) || visibleTabs[0];
    if (!tab) {
      return throwError('No dashboards are currently available for this group.');
    }
    return this.configService.loadDashboard(tab.dashboardKey);
  }

  private isVisible(tab: ReportDashboardGroupTabDefinition): boolean {
    if (!tab.visibleExpression) {
      return true;
    }
    return this.expressionState.evaluateExpressionWithResolvedData(tab.visibleExpression, {}, this.expressionContext || {});
  }
}
